import { useAuthQuery } from '#lib/hooks/use-auth-query.js';
import { fetchAndParse, jsonHeaders } from '#lib/helpers/fetch.js';
import { getAuthHeader } from '#lib/hooks/use-auth.jsx';
import useMutation from '#lib/hooks/use-mutation.js';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

async function getAll(search) {
    const authHeaders = await getAuthHeader();

    const response = await fetch(`${API_URL}/p/user?query=${search ?? ''}`, {
        method: 'GET',
        headers: { ...jsonHeaders, ...authHeaders }
    });
    return await response.json();
}

async function get(id) {
    const authHeaders = await getAuthHeader();
    return await fetchAndParse(`${API_URL}/p/user/${id}`, {
        method: 'GET',
        headers: { ...jsonHeaders, ...authHeaders }
    });
}

async function destroy(id) {
    const authHeaders = await getAuthHeader();
    return await fetchAndParse(`${API_URL}/p/user/${id}`, {
        method: 'DELETE',
        headers: { ...jsonHeaders, ...authHeaders }
    });
}

async function create(body) {
    const authHeaders = await getAuthHeader();
    return await fetchAndParse(`${API_URL}/p/user`, {
        method: 'POST',
        headers: { ...jsonHeaders, ...authHeaders },
        body: JSON.stringify(body)
    });
}

async function update(body) {
    const authHeaders = await getAuthHeader();
    return await fetchAndParse(`${API_URL}/p/user`, {
        method: 'PUT',
        headers: { ...jsonHeaders, ...authHeaders },
        body: JSON.stringify(body)
    });
}

async function resetPassword(body) {
    const authHeaders = await getAuthHeader();
    return await fetchAndParse(`${API_URL}/p/password/reset`, {
        method: 'POST',
        headers: { ...jsonHeaders, ...authHeaders },
        body: JSON.stringify(body)
    });
}

export default function useUserQueries(search, userId, lang) {
    const queryClient = useQueryClient();
    const [userData, setUserData] = useState(null);

    const { data, refetch, ...queryProps } = useAuthQuery({
        queryKey: ['users', search],
        queryFn: () => getAll(search),
        staleTime: Infinity,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
        cacheTime: 0
    });

    const createUser = useMutation({
        mutationKey: ['createUser'],
        mutationFn: create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
            toast.success(_t.global.success());
        },
        onError: (err) => toast.error(err.message),
        enabled: false
    });

    const deleteUser = useMutation({
        mutationKey: ['deleteUser'],
        mutationFn: destroy,
        onSuccess: () => toast.success(_t.global.success()),
        onError: () => toast.error(_t.users.deleteError()),
        updateSuccess: (prev) => { },
        enabled: false
    });

    const getUser = useMutation({
        mutationKey: ['user'],
        mutationFn: async (id) => {
            const { model } = await get(id);
            setUserData(model);
        },
        onError: (err) => toast.error(err.message),
        enabled: false
    });

    const resetUserPassword = useMutation({
        mutationKey: ['resetPassword', userId],
        mutationFn: resetPassword,
        onSuccess: () => toast.success(_t.global.success()),
        onError: (err) => toast.error(err.message),
        enabled: false
    });

    const updateUser = useMutation({
        mutationKey: ['updateUser'],
        mutationFn: update,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
            toast.success(_t.global.success());
            refetch();
        },
        onError: (err) => toast.error(err.message),
        enabled: false
    });

    return {
        userData,
        usersList: data?.results,
        availableUsers: data?.availableUsers,
        totalUsers: data?.totalUsers,
        createUser,
        deleteUser,
        getUser,
        refetch,
        resetUserPassword,
        updateUser,
        ...queryProps
    };
}
